import { FRAME_DURATION_SEC } from '../../core/types.js';
import { adCutAnchor, alignToChunkStart, cutDueWithoutFrames, videoStartFromFrame, } from '../../core/chunk-align.js';
/**
 * 네이티브 HLS 기반 `PlayerAdapter` — `<video src="…m3u8">` 두 개를 번갈아 쓰는 더블 버퍼링.
 *
 * **이것이 `docs/05-receiver-sdk.md` 가 기다리던 두 번째 어댑터다.** 그 문서의 완료 조건
 * 「웹 어댑터 교체만으로 동작하며 코어 코드는 무변경」이 여기서 처음 실증된다 —
 * `core/` 는 한 줄도 열리지 않는다 (절대 규칙 6·36).
 *
 * ## 왜 필요한가 (2026-08-09 실기)
 *
 * **iPhone 에는 MSE 가 없어 hls.js 가 아예 돌지 않는다.** `MediaSource`·
 * `ManagedMediaSource`·`SourceBuffer` 셋 다 없고(iPhone 의 MSE 는 iOS 17.1 의
 * `ManagedMediaSource` 가 처음이다), 그 상태에서 `new Hls()` 를 부르면 **던지지도 오류를
 * 내지도 않고 조용히 멈춘다** — `MEDIA_ATTACHED` 가 오지 않아 `arm()` 이 15초를 다 쓰고
 * `PLAYBACK_FAILED` 가 된다. 기전과 실측은 `docs/30-ios-spike-result.md` §1.
 *
 * **iOS 가 HLS 를 못 트는 것이 아니다.** 같은 자산을 `<video src>` 는 재생한다
 * (`loadedmetadata` 255ms · `readyState 4` · 1280×720 · 전체화면 강탈 없음). 인코딩 규격이
 * 고정값(720p/H.264 main/AAC/2초 세그먼트, 절대 규칙 4)이라 네이티브가 그대로 튼다 —
 * 규격을 자산마다 바꿨다면 이 어댑터가 성립하지 않는다.
 *
 * ## `hls-player.ts` 와 무엇이 다른가 — 셋뿐이다
 *
 * | | hls.js | 여기 |
 * |---|---|---|
 * | 적재 | `new Hls()` → `attachMedia` → `loadSource` | `el.src = url` → `el.load()` |
 * | `videoSpan` | `LEVEL_LOADED` 의 `totalduration` | **m3u8 을 직접 받아 `#EXTINF` 를 센다** |
 * | 정리 | `hls.destroy()` | `removeAttribute('src')` → `load()` |
 *
 * 나머지(더블 버퍼·꼬리 프리롤·예측 스왑·워치독·`ended` 백스톱·T_freeze 산술)는 **같은
 * 값과 같은 순서다.** M0·M6 이 실측으로 얻은 것이라 여기서 임의로 바꾸면 무갭이 깨지고,
 * 깨진 것을 알아채기 어렵다 — `TransitionMetrics.path` 가 유일한 관찰 창이다.
 *
 * > **그래서 이 파일은 `hls-player.ts` 와 타이밍 기계를 중복해서 갖는다.** 한쪽만 고쳐지면
 * > 증상이 「iOS 에서만 채널이 멈춘다」이고 **노트북 하네스는 그것을 볼 수 없다.**
 * > 공유 기반 클래스로 뽑지 않은 것은 v12 N3 의 완료 조건 ⑤ 가 「기존 경로를 건드리지
 * > 않았다」를 요구하기 때문이다 — 측정된 타이밍 코드를 리팩터링하는 것이 이 저장소에서
 * > 가장 위험한 변경이고, 그 판단은 두 어댑터가 실제로 갈릴 필요가 있는지 본 뒤에 한다.
 * > **타이밍 로직을 고칠 때는 반드시 두 파일을 함께 연다.**
 *
 * ## N1 이 확정한 것 (`docs/30`)
 *
 * - rVFC 있음 · 두 `<video>` 동시 재생 됨 · **`opacity:0` 버퍼가 합성된다**(표시 45 / 디코드 48)
 *   → **저하 모드가 아니라 더블 버퍼가 성립한다**
 * - `await` 뒤의 소리 있는 `play()` 가 허용된다 (셸 안에서 **11,121ms** 대기 후에도)
 *   → `docs/29` D6 요점 4(제스처 유실 대비)를 **만들지 않는다**
 */
/** 적재 상한. hls.js 경로와 같은 값이다. */
const ARM_TIMEOUT_MS = 15_000;
/**
 * 적재를 **몇 번까지** 시도하는가(첫 시도 포함). `2` 는 「한 번은 되살린다」다.
 *
 * ★ **웹 어댑터에는 이것이 있었고 여기에는 없었다** (2026-09-22 아이폰 실기). hls.js 쪽은
 * 치명적 오류를 `startLoad()`·`recoverMediaError()` 로 되살리는데(`FATAL_NET_RETRIES`),
 * 네이티브 경로에는 되살릴 자리가 없어 **같은 망 흔들림이 노트북에서는 조용히 넘어가고
 * 아이폰에서만 오류 화면**이 됐다.
 */
const ARM_ATTEMPTS = 2;
/** 워치독: 마지막 프레임 이후 이만큼 새 프레임이 없으면 스왑한다. 1.5 프레임. */
const WATCHDOG_MS = 1.5 * FRAME_DURATION_SEC * 1000;
/** 매니페스트 조회 상한. 실패해도 적재는 계속된다 — 예측 스왑만 잃는다. */
const MANIFEST_TIMEOUT_MS = 5_000;
/**
 * 「첫 프레임이 합성됐다」를 rVFC 로 기다리는 상한.
 *
 * hls.js 경로는 이 증명(스파이크 `armedProof`)을 무기한 기다린다. 여기서 상한을 두는 이유는
 * **정지 상태의 rVFC 가 발화한다는 보장이 없기 때문이다** — N1 이 잰 것은 전부 재생 중의
 * 발화였고(`docs/30` §3.2), `prepareNext()` 의 대기 버퍼는 재생하지 않는다. 무기한 기다리면
 * 그 자리에서 15초를 태우고 `PLAYBACK_FAILED` 가 된다.
 *
 * **증명을 잃어도 크게 잃지 않는다.** 대기 버퍼는 스왑 전에 꼬리 프리롤로 약 150ms 재생되고
 * (그동안 rVFC 가 돈다) 그것이 실제 합성 증거다. 증명 없이 노출되는 경로는 프리롤이 실패한
 * `no-preroll`·`cold` 뿐이고, 그 둘은 이미 강등된 경로다.
 */
const FRAME_PROOF_GRACE_MS = 400;
/** 탐색이 끝난 프레임을 몇 장까지 기다리는가 (v27/TG16 · `hls-player.ts` 와 같은 값) */
const SEEK_SETTLE_FRAMES = 8;
export class NativeHlsPlayerAdapter {
    buffers;
    front = 0;
    tail = null;
    /** 스왑·무장 세대. 오래된 프레임 콜백과 워치독을 무효화한다. */
    gen = 0;
    swapping = false;
    /**
     * rVFC 가 없으면 **저하 모드**다 (`docs/29` D6). 무갭을 포기하고 채널이 끊기지 않는
     * 것만 지킨다 — 프리롤은 `timeupdate`(약 4Hz)가 몰고 스왑은 `ended` 백스톱이 받는다.
     * iOS 15.4+ 에는 rVFC 가 있으므로(N1 확인) 데모 기기에서는 이 모드로 가지 않는다.
     */
    hasRvfc;
    /**
     * ★ **엘리먼트에 붙인 리스너를 뗄 수 있게 한다** (v19).
     *
     * 전에는 엘리먼트가 어댑터와 함께 죽어서 뗄 필요가 없었다. **입양하면 엘리먼트가
     * 살아남으므로** 떼지 않으면 전환마다 쌓이고, 그것이 절대 규칙 27 이 경고하는
     * 「세 번째 전환쯤부터 `start` 가 두 번씩 나간다」다.
     */
    listeners = new AbortController();
    /** 엘리먼트를 남에게 받았는가. `destroy()` 가 뗄지 되돌릴지를 이 값이 가른다. */
    adopted;
    vsyncMs = 1000 / 60;
    timeUpdateCb = null;
    errorCb = null;
    transitionCb = null;
    constructor(opts) {
        this.hasRvfc = typeof HTMLVideoElement.prototype.requestVideoFrameCallback === 'function';
        this.adopted = opts.buffers !== undefined;
        this.buffers = [
            this.createBuffer(opts.container, opts.buffers?.[0]),
            this.createBuffer(opts.container, opts.buffers?.[1]),
        ];
        this.reveal(0);
        void this.measureVsync();
    }
    get frontBuf() {
        return this.buffers[this.front];
    }
    /** 대기 버퍼. `opacity: 0` 이고 음소거 상태다. */
    get backBuf() {
        return this.buffers[this.front === 0 ? 1 : 0];
    }
    get backIndex() {
        return this.front === 0 ? 1 : 0;
    }
    // ── 적재·재생 ───────────────────────────────────────────────────────────
    async load(url, startAtSec) {
        const b = this.frontBuf;
        await this.arm(b, url, startAtSec);
        this.reveal(this.front);
    }
    async play() {
        const b = this.frontBuf;
        b.el.muted = false;
        await b.el.play();
        b.playing = true;
        this.startWatch(b);
    }
    pause() {
        const b = this.frontBuf;
        b.el.pause();
        b.playing = false;
        // v35. 꼬리 창에서 멈추면 워치독이 정지 중에 스왑을 터뜨린다 — 근거는 `rewindPreroll`.
        this.rewindPreroll();
    }
    /** v35. 앞 버퍼의 위치만 옮긴다. 상한은 코어가 이미 걸었다(절대 규칙 91) */
    seek(sec) {
        this.rewindPreroll();
        this.frontBuf.el.currentTime = sec;
    }
    /**
     * ★ v35. **꼬리 프리롤을 「아직 시작 전」으로 되돌린다** (`docs/71` §2.2 · 절대 규칙 92).
     *
     * 꼬리 `prerollSec` 안에서는 대기 버퍼가 음소거로 이미 돌고 워치독이 무장된다. 그 창에서
     * ① 정지하면 프레임이 멈춰 워치독이 **정지 중에 스왑을 터뜨리고** ② 뒤로 가면 대기 버퍼가
     * 그동안 계속 돌아 **다음 것이 몇 초 들어간 데서 시작한다.** 둘 다 증상이 원인을 말하지
     * 않는다(「정지가 안 먹는다」 · 「다음 회차 첫 장면이 없다」).
     *
     * 컷 위치(`swapAt`·`prerollAt`)는 mediaTime 축이라 그대로 두고, 재생이 다시 그 자리에
     * 닿으면 프레임 체인이 프리롤을 다시 건다.
     */
    rewindPreroll() {
        const tail = this.tail;
        if (tail === null || !tail.prerolled)
            return;
        if (tail.watchdog !== null) {
            clearTimeout(tail.watchdog);
            tail.watchdog = null;
        }
        tail.prerolled = false;
        tail.prerollStartedAt = null;
        const back = this.backBuf;
        back.el.pause();
        back.playing = false;
        if (tail.backStartAt !== null)
            back.el.currentTime = tail.backStartAt;
        tail.backStartAt = null;
    }
    stop() {
        this.gen += 1;
        this.clearTail();
        /*
         * **`swapping` 을 여기서 내린다** (v19). 스왑의 지연 정리가 그것을 내려 주는데, 위에서
         * `gen` 을 올린 순간 그 콜백은 **가드에 걸려 돌아간다** — 내려 주는 자리가 사라지므로
         * 여기서 내리지 않으면 `performSwap` 이 영원히 `false` 를 돌려주고 **채널이 멈춘다.**
         * `TASKS-V12` 가 어댑터 재사용에서 경계하던 「굳는다」가 정확히 이 모양이다.
         */
        this.swapping = false;
        for (const b of this.buffers) {
            b.el.pause();
            b.playing = false;
            b.watching = false;
            this.detach(b);
        }
    }
    async prepareNext(url, startAtSec) {
        const b = this.backBuf;
        if (b.url === url && b.ready)
            return;
        // ★ 스왑을 기다리는 적재를 앞지르지 않는다 — 근거는 `hls-player.ts` 의 같은 자리.
        if (b.arming?.forSwap === true)
            return;
        b.el.muted = true;
        await this.arm(b, url, startAtSec);
    }
    // ── 꼬리 스왑 ───────────────────────────────────────────────────────────
    /**
     * v21. **`hls-player.ts` 의 같은 메서드와 짝이다.** 갈리는 것은 세그먼트 길이의 출처
     * 하나뿐이다 — 저쪽은 `LEVEL_LOADED`, 이쪽은 `parseExtInf` 로 직접 받은 것.
     *
     * **재무장 규약 둘도 같다** — ① 앞선 무장을 먼저 끊는다 ② 앵커를 못 구하면 **조용히
     * 거른다**(`onAbandoned` 로 흘리면 `recoverByLoad` 가 회차를 통째로 넘긴다).
     */
    armTailSwap(opts) {
        this.clearTail();
        const front = this.frontBuf;
        const lastFrame = lastFrameMediaTime(front);
        if (opts.atSec !== undefined) {
            const cut = adCutAnchor(front.segmentDurations, front.videoStart, front.videoSpan, opts.atSec);
            if (cut === null)
                return;
            this.tail = {
                opts,
                prerollAt: cut.mediaTime - opts.prerollSec,
                swapAt: cut.mediaTime,
                cutAtSec: cut.cutAtSec,
                prerolled: false,
                prerollStartedAt: null,
                backStartAt: null,
                watchdog: null,
            };
            this.startWatch(front);
            return;
        }
        // 예측 스왑을 쓸 수 없을 때만 `duration` 으로 폴백한다. `duration` 은 비디오 끝과 오디오
        // 끝 중 큰 쪽이므로 프리롤이 **일찍** 발화하는 안전한 방향이고, 스왑 판정에는 쓰지 않는다.
        const fallbackEnd = Number.isFinite(front.el.duration)
            ? front.el.duration - FRAME_DURATION_SEC
            : null;
        const anchor = lastFrame ?? fallbackEnd;
        if (anchor === null) {
            opts.onAbandoned('비디오 끝을 추정할 수 없다 (duration 도 EXTINF 도 없음)');
            return;
        }
        this.tail = {
            opts,
            prerollAt: anchor - opts.prerollSec,
            swapAt: lastFrame,
            cutAtSec: undefined,
            prerolled: false,
            prerollStartedAt: null,
            backStartAt: null,
            watchdog: null,
        };
        this.startWatch(front);
    }
    /**
     * 정책상 초 → 청크 경계로 내린 mediaTime. **`hls-player.ts` 의 같은 함수와 같은 규칙**
     * 이어야 한다 — 어긋나면 같은 지점에서 기기마다 다르게 잘린다.
     */
    cancelTailSwap() {
        this.gen += 1;
        this.clearTail();
    }
    async swapNow(trigger) {
        this.cancelTailSwap();
        const requestedAt = performance.now();
        // 자연 스왑이 진행 중이면 그것이 끝나기를 기다린다 — 기다리지 않으면 `performSwap` 이
        // 재진입 가드에 걸려 **화면은 그대로인데 코어는 인덱스를 올린다.**
        await this.settleSwap();
        const aLastEdt = this.frontBuf.lastFrameEdt;
        const back = this.backBuf;
        let path;
        if (back.ready) {
            path = back.playing ? 'preroll' : 'no-preroll';
        }
        else {
            if (!back.url)
                throw new Error('대기 버퍼에 URL 이 없다. prepareNext 가 필요하다.');
            // **스왑이 기다리는 적재다** — 근거는 `hls-player.ts` 의 같은 자리.
            await this.arm(back, back.url, undefined, true);
            path = 'cold';
        }
        if (!this.performSwap(path, trigger, aLastEdt, requestedAt)) {
            throw new Error('스왑이 재진입 가드에 걸렸다.');
        }
    }
    async settleSwap() {
        for (let i = 0; this.swapping && i < 10; i += 1) {
            await new Promise((resolve) => setTimeout(resolve, 0));
        }
    }
    // ── 콜백 등록 ───────────────────────────────────────────────────────────
    onTimeUpdate(cb) {
        this.timeUpdateCb = cb;
    }
    onError(cb) {
        this.errorCb = cb;
    }
    onTransition(cb) {
        this.transitionCb = cb;
    }
    destroy() {
        this.stop();
        // **리스너를 먼저 뗀다.** 입양한 엘리먼트는 살아남으므로 떼지 않으면 전환마다 쌓인다.
        this.listeners.abort();
        for (const b of this.buffers) {
            if (this.adopted) {
                // **되돌리기만 한다.** `stop()` 이 이미 `pause()` 와 소스 정리를 했고, 남은 것은
                // 다음 어댑터가 깨끗한 상태를 받게 하는 것뿐이다 — 생성자의 `reveal(0)` 이
                // `opacity`/`zIndex` 를 다시 정하므로 여기서는 소리만 되돌린다.
                b.el.muted = true;
            }
            else {
                b.el.remove();
            }
        }
    }
    debugSnapshot() {
        const front = this.frontBuf;
        const back = this.backBuf;
        return {
            frontIndex: this.front,
            nextReady: back.ready,
            nextPlaying: back.playing,
            nextUrl: back.url,
            tailArmed: this.tail !== null,
            prerolled: this.tail?.prerolled ?? false,
            armedAtSec: this.tail?.cutAtSec ?? null,
            videoStart: front.videoStart,
            videoSpan: front.videoSpan,
            duration: Number.isFinite(front.el.duration) ? front.el.duration : null,
            vsyncMs: this.vsyncMs,
        };
    }
    // ── 내부 ───────────────────────────────────────────────────────────────
    createBuffer(container, existing) {
        // **입양하면 만들지 않는다** — 허가는 엘리먼트에 붙어 있고, 그것이 이 옵션의 전부다.
        // `appendChild` 는 이미 다른 부모에 있어도 옮겨 준다(같은 객체가 유지된다).
        const el = existing ?? document.createElement('video');
        // **`playsinline` 이 없으면 iPhone 이 영상을 자기 전체화면 플레이어로 가져간다.**
        // 그러면 오버레이도 미니 플레이어도 성립하지 않는다 — 절대 규칙 14 가 iOS 에서
        // 깨지는 자리다. 셸의 `allowsInlineMediaPlayback` 과 짝이다 (`docs/29` §6.1-2).
        el.playsInline = true;
        el.setAttribute('webkit-playsinline', 'true');
        el.preload = 'auto';
        el.muted = true;
        // **`opacity` 로만 숨긴다** (E0). N1 이 iOS 에서도 이것을 확인했다 —
        // 숨긴 쪽 표시 45 / 디코드 48 로 보이는 쪽(47/47)과 거의 같다 (`docs/30` §3.2).
        el.style.cssText =
            'position:absolute;top:0;left:0;width:100%;height:100%;' +
                'object-fit:contain;background:#000;opacity:0;z-index:1;';
        el.addEventListener('timeupdate', () => {
            if (this.frontBuf.el !== el)
                return;
            this.timeUpdateCb?.(el.currentTime);
            // 저하 모드의 프리롤 트리거. rVFC 가 있으면 프레임 체인이 대신한다.
            if (!this.hasRvfc)
                this.stepDegraded(el.currentTime);
            // v33-e. 프레임이 끊겼으면(숨은 창 · 저하 모드) 컷을 여기서 판정한다 — `hls-player.ts` 와 한 벌.
            this.cutWithoutFrames(el);
        }, { signal: this.listeners.signal });
        el.addEventListener('error', () => {
            // **src 를 뗄 때 나는 오류는 우리가 만든 것이다.** 코어의 `onError` 는 그것을
            // `PLAYBACK_FAILED` 로 올려 화면을 오류로 덮으므로, 멀쩡한 채널이 전환할 때마다
            // 고장 난 것처럼 보인다. 소스가 없는 상태의 오류는 삼킨다.
            if (!el.getAttribute('src'))
                return;
            this.errorCb?.(el.error);
        }, { signal: this.listeners.signal });
        // **폴백 3층 — 최후 백스톱.** S1(약 150ms)이지만 **채널은 계속된다.**
        // 저하 모드에서는 이것이 유일한 스왑 경로다.
        el.addEventListener('ended', () => {
            if (this.frontBuf.el !== el || this.tail === null || this.swapping)
                return;
            this.performSwap('ended', 'natural', this.frontBuf.lastFrameEdt, null);
        }, { signal: this.listeners.signal });
        container.appendChild(el);
        return {
            el,
            url: null,
            loadToken: 0,
            ready: false,
            playing: false,
            videoStart: null,
            videoSpan: null,
            segmentDurations: null,
            abortArm: null,
            arming: null,
            frames: 0,
            lastFrameEdt: null,
            lastFrameAt: null,
            watching: false,
            watchGen: 0,
        };
    }
    /**
     * 버퍼를 비운다. `hls.destroy()` 자리다.
     *
     * **`src` 를 떼는 것만으로는 부족하다** — `load()` 를 불러야 자원 선택 알고리즘이 다시
     * 돌아 디코더와 네트워크가 실제로 풀린다. iPhone 7 의 메모리를 생각하면 남겨 둘 수 없다.
     * 소스도 `<source>` 자식도 없으면 `error` 가 아니라 `NETWORK_EMPTY` 로 끝난다(HTML 명세) —
     * 그래도 위 `error` 리스너가 소스 없는 상태를 삼키므로 두 겹이다.
     *
     * 프레임 체인도 함께 끊는다 — 세대를 올려 예약된 콜백이 다음 발화에서 스스로 반환하게 한다.
     */
    detach(b) {
        // ★ **적재 중이던 약속을 여기서 끝낸다** — 근거는 `Buffer.abortArm`. 아래에서 `src` 를
        // 떼면 그 약속에는 `loadeddata` 가 다시 오지 않으므로(그리고 `error` 는 소스 없는
        // 상태라 삼켜진다), 끝내 주지 않으면 15초 뒤에야 스스로 거절한다.
        const abort = b.abortArm;
        b.abortArm = null;
        b.arming = null;
        abort?.(new Error(`적재가 앞질러졌다: ${b.url ?? ''}`));
        /*
         * ★ v33-c. **떼는 순간 그 적재는 무효다.** 토큰을 올려 두지 않으면 방금 거절된 약속이
         * 「아직 내 차례」로 보여 **되살리기가 `stop()` 뒤에도 돈다** — 채널을 끈 뒤 영상이
         * 되살아나는 모양이고, 증상은 「끈 채널의 소리가 난다」 하나다.
         */
        b.loadToken += 1;
        b.el.removeAttribute('src');
        b.el.load();
        b.watchGen += 1;
        b.watching = false;
        b.ready = false;
        b.videoStart = null;
        b.videoSpan = null;
        b.segmentDurations = null;
        b.frames = 0;
        b.lastFrameEdt = null;
        b.lastFrameAt = null;
    }
    /**
     * 적재. 미디어와 매니페스트를 **나란히** 띄우고 둘 다 기다린다.
     *
     * 매니페스트를 함께 기다리는 이유: `armTailSwap()` 은 `load()`·`play()` 직후 **한 번만**
     * 불리므로, 그 시점에 `videoSpan` 이 아직 없으면 그 항목은 예측 스왑을 통째로 잃는다.
     * 매니페스트는 몇 KB 이고 세그먼트와 같은 오리진이라 미디어 적재(N1 실측 255ms)보다
     * 먼저 끝난다 — 기다려서 늘어나는 시간이 사실상 없다.
     */
    arm(b, url, startAtSec, forSwap = false) {
        const inFlight = b.arming;
        if (inFlight !== null && inFlight.url === url && inFlight.startAtSec === startAtSec) {
            // 같은 적재가 이미 돈다 — 다시 받지 않고 올라탄다 (`ArmInFlight`).
            if (forSwap)
                inFlight.forSwap = true;
            return inFlight.promise;
        }
        // `armFresh` 의 첫 줄이 `detach()` 라 **거기서 옛 `arming` 이 지워진다.** 그래서 새
        // 것을 그 **뒤에** 건다 — `armFresh` 는 async 라 첫 `await` 까지 동기로 돈다.
        const promise = this.armFresh(b, url, startAtSec);
        const entry = { url, startAtSec, forSwap, promise };
        b.arming = entry;
        const clear = () => {
            if (b.arming === entry)
                b.arming = null;
        };
        void promise.then(clear, clear);
        return promise;
    }
    async armFresh(b, url, startAtSec) {
        this.detach(b);
        b.url = url;
        b.playing = false;
        const token = (b.loadToken += 1);
        const resuming = startAtSec !== undefined && startAtSec > 0;
        // **이어보기면 매니페스트를 먼저 기다린다.** 세그먼트 길이가 있어야 청크 경계를 아는데,
        // 병렬로 두면 `loadeddata` 가 먼저 와서 정렬 없이 0 부터 재생한다. 처음부터 재생하는
        // 경로에서는 예전 그대로 병렬이다 — 그 자리의 지연을 늘리지 않는다.
        if (resuming) {
            const durations = await fetchSegments(url);
            if (b.loadToken !== token)
                return;
            if (durations !== null) {
                b.videoSpan = durations.reduce((a, c) => a + c, 0);
                b.segmentDurations = durations;
                // 매니페스트를 못 받으면 `durations` 가 null 이고 정렬 없이 처음부터 간다.
                // **재생을 막지 않는다** — 이어보기를 잃는 편이 검은 화면보다 낫다.
                await this.armMediaOrRetry(b, url, token, alignToChunkStart(durations, startAtSec).startSec);
                return;
            }
            await this.armMediaOrRetry(b, url, token);
            return;
        }
        const manifest = fetchSegments(url).then((durations) => {
            // 그사이 다른 URL 로 다시 적재됐으면 남의 값이다.
            if (b.loadToken === token && durations !== null) {
                b.videoSpan = durations.reduce((a, c) => a + c, 0);
                b.segmentDurations = durations;
            }
        });
        await Promise.all([this.armMediaOrRetry(b, url, token), manifest]);
    }
    /**
     * ★ v33-c. **적재가 죽으면 한 번은 다시 건다** (2026-09-22 아이폰 실기 · `docs/68` §2.0-d).
     *
     * 무대 기록은 「첫 재생은 되는데 **채널을 서너 번 바꾸면**」이었다. 그때 나오는 것이
     * `적재 시간 초과`(15초)이고, 그 15초는 시청자에게 **고장**이다 — 실제로는 한 번 더
     * 걸면 되는 상태다(같은 주소가 그 전에도 그 뒤에도 재생됐다 · `Event` 기록).
     *
     * **같은 `src` 를 그대로 다시 대입하지 않는다** — 값이 같으면 WebKit 이 「바뀐 것이
     * 없다」로 보고 다시 적재하지 않는다. `detach()` 가 쓰는 초기화를 그대로 쓴다.
     *
     * **앞질러졌으면 되살리지 않는다**(`retriesArm` 의 토큰 검사) — 시청자가 이미 다른 것을
     * 골랐거나 화면이 꺼진 뒤다. 그 자리에서 되살리면 **고른 적 없는 회차가 튼다.**
     */
    async armMediaOrRetry(b, url, token, alignedStartSec = 0) {
        for (let attempts = 1;; attempts += 1) {
            try {
                await this.armMedia(b, url, alignedStartSec);
                return;
            }
            catch (e) {
                if (!retriesArm({ token, currentToken: b.loadToken, attempts }))
                    throw e;
                b.el.removeAttribute('src');
                b.el.load();
            }
        }
    }
    /**
     * `<video>` 에 소스를 걸고 첫 프레임까지 확인한다. 스파이크 `armedProof` 의 네이티브 판이고,
     * 다른 점은 rVFC 대기에 상한이 있다는 것 하나다 — 위 `FRAME_PROOF_GRACE_MS`.
     */
    armMedia(b, url, alignedStartSec = 0) {
        return new Promise((resolve, reject) => {
            let settled = false;
            let grace = null;
            const finish = (err) => {
                if (settled)
                    return;
                settled = true;
                clearTimeout(timer);
                if (grace !== null)
                    clearTimeout(grace);
                // 내가 건 손잡이일 때만 뗀다 — 이미 다음 적재가 자기 것을 걸어 뒀을 수 있다.
                if (b.abortArm === finish)
                    b.abortArm = null;
                b.el.removeEventListener('loadeddata', onLoaded);
                b.el.removeEventListener('error', onError);
                if (err)
                    reject(err);
                else
                    resolve();
            };
            // `detach()` 가 이것을 부른다 (`Buffer.abortArm` 의 ★).
            b.abortArm = finish;
            // 타임아웃에 상태를 함께 남긴다 — `networkState 2`(LOADING)면 네트워크가 매달린
            // 것이고 `3`(NO_SOURCE)이면 소스가 거절된 것이라 대응이 다르다. N1 하네스가
            // 같은 이유로 같은 값을 남겼다.
            const timer = setTimeout(() => {
                finish(new Error(`적재 시간 초과: ${url} (networkState=${String(b.el.networkState)} ` +
                    `readyState=${String(b.el.readyState)})`));
            }, ARM_TIMEOUT_MS);
            const onError = () => {
                const code = b.el.error === null ? 'unknown' : `MEDIA_ERR_${String(b.el.error.code)}`;
                finish(new Error(`네이티브 HLS 적재 실패: ${url} (${code})`));
            };
            /*
             * ★★ v27/TG16 — **탐색이 끝난 프레임에서만 눈금을 잡는다** (`hls-player.ts` 와
             * 같은 고침 · 규칙 47 의 「어댑터는 둘이다」).
             *
             * `videoStart` 는 「위치 0 에 해당하는 mediaTime」이지 **첫 프레임의 것이 아니다.**
             * 탐색은 비동기라 그 프레임이 탐색 **전**의 것일 수 있고, 그러면 `videoStart` 가
             * 크게 음수가 되어 회차 끝이 **이어보기 지점 그 자리**로 내려앉는다 — 붙자마자
             * 꼬리 스왑이 터진다(`chunk-align.ts` 의 ★).
             *
             * **한쪽만 고치면 그 기기에서만 남는다** — 여기가 아이폰의 경로다.
             */
            const settleReady = (mediaTime, tries = 0) => {
                if (grace !== null) {
                    clearTimeout(grace);
                    grace = null;
                }
                const start = videoStartFromFrame(mediaTime, alignedStartSec);
                if (start === null && this.hasRvfc && tries < SEEK_SETTLE_FRAMES) {
                    /*
                     * ★★★★ v33-c — **유예를 되건다. 이 재귀에는 그것이 없었다** (2026-09-22 실기).
                     *
                     * **멈춘 `<video>` 는 새 프레임을 내지 않는다.** 적재 중 버퍼는 멈춰 있고, 위
                     * `onLoaded` 의 탐색이 내놓는 프레임은 **한 장뿐**이다. 그 한 장이 탐색 **전**의
                     * 것이면(`start === null`) 여기서 다음 장을 기다리는데 **다음 장이 영영 없다** —
                     * `ARM_TIMEOUT_MS`(15초)를 다 쓰고 `적재 시간 초과`로 죽는다.
                     *
                     * 무대 사진이 그것을 그대로 말했다:
                     * `적재 시간 초과: …/index.m3u8 (networkState=2 readyState=4)` —
                     * **`readyState=4` 는 데이터가 다 왔다는 뜻**이다. 네트워크가 아니라 프레임을
                     * 기다리다 굳은 것이고, 그래서 **첫 재생은 멀쩡한데 채널을 서너 번 바꾸면** 난다:
                     * 그때쯤 그 채널들에 이어보기 지점이 생겨 `alignedStartSec > 0` 이 되고,
                     * 탐색이 생기면서 「탐색 전 프레임」을 만날 창이 열린다.
                     *
                     * 웹 어댑터가 라이브에서 겪은 것과 **같은 종류**다(`hls-player.ts` 의 ★★★★) —
                     * 거기서는 탐색이 아예 없어 첫 대기가 굳었고, 여기서는 첫 대기에만 유예가
                     * 있어 **두 번째부터** 굳는다.
                     */
                    grace = setTimeout(() => {
                        settleReady(b.el.currentTime, SEEK_SETTLE_FRAMES);
                    }, FRAME_PROOF_GRACE_MS);
                    b.el.requestVideoFrameCallback((_now, meta) => {
                        settleReady(meta.mediaTime, tries + 1);
                    });
                    return;
                }
                // 끝내 못 받으면 **0 으로 물러난다** — 프라이밍만큼 한 프레임 **일찍** 자르는
                // 안전한 방향이다(늦으면 회차 끝에서 채널이 선다).
                b.videoStart = start ?? 0;
                b.ready = true;
                finish();
            };
            const onLoaded = () => {
                if (b.el.readyState < 2)
                    return;
                const capture = () => {
                    if (!this.hasRvfc) {
                        // rVFC 가 없으면 프레임의 mediaTime 을 알 수 없다. `currentTime` 은 탐색이
                        // 끝난 뒤라 목표와 같고, 순수 함수가 그것을 그대로 통과시킨다.
                        settleReady(b.el.currentTime);
                        return;
                    }
                    // 유예를 넘기면 `currentTime` 으로 대신한다. 이 값은 실제 첫 프레임보다 **작거나
                    // 같아** 마지막 프레임 판정을 앞당기는 안전한 방향이다 (A 를 한 프레임 자를 뿐이다).
                    // 유예로 들어오면 **더 기다리지 않는다** — `tries` 를 상한으로 주어 곧바로 settle 한다.
                    grace = setTimeout(() => {
                        settleReady(b.el.currentTime, SEEK_SETTLE_FRAMES);
                    }, FRAME_PROOF_GRACE_MS);
                    b.el.requestVideoFrameCallback((_now, meta) => {
                        settleReady(meta.mediaTime);
                    });
                };
                // **`loadeddata` 에서 준다.** `canplay` 에서 주면 iPhone 에서 첫 프레임이 보였다가
                // 튄다 (`docs/38` §9 의 10).
                if (alignedStartSec > 0 && b.el.currentTime < alignedStartSec) {
                    b.el.currentTime = alignedStartSec;
                }
                capture();
            };
            b.el.addEventListener('loadeddata', onLoaded);
            b.el.addEventListener('error', onError);
            // N1 하네스가 실기에서 통과시킨 순서 그대로다 — `src` 를 걸고 `load()` 를 부른다.
            b.el.src = url;
            b.el.load();
        });
    }
    /**
     * 버퍼의 프레임 체인. 마지막 프레임 시각을 기록하고, 꼬리가 무장돼 있으면 **이 콜백
     * 안에서** 프리롤과 스왑을 수행한다. 체인은 버퍼당 하나만 돈다.
     */
    startWatch(b) {
        if (!this.hasRvfc || b.watching)
            return;
        b.watching = true;
        // **전역 `gen` 이 아니라 버퍼별 세대를 본다** — 근거는 `Buffer.watchGen` 주석.
        const gen = b.watchGen;
        const step = (now, meta) => {
            if (!b.watching || gen !== b.watchGen)
                return;
            b.frames += 1;
            b.lastFrameEdt = meta.expectedDisplayTime;
            b.lastFrameAt = now;
            const tail = this.tail;
            const isFront = this.frontBuf === b;
            if (tail && isFront && !this.swapping) {
                if (!tail.prerolled && meta.mediaTime >= tail.prerollAt) {
                    tail.prerolled = true;
                    tail.prerollStartedAt = performance.now();
                    this.startPreroll();
                    // 워치독은 **프리롤 이후에만** 무장한다 — 영상 중간의 버퍼 언더런을 전환으로
                    // 오인하지 않기 위함이다.
                    this.armWatchdog(tail);
                }
                if (tail.swapAt !== null && meta.mediaTime >= tail.swapAt - 0.5 * FRAME_DURATION_SEC) {
                    // 이 콜백의 expectedDisplayTime 이 t_A_last 의 확정값이다. 사후 추정하지 않는다.
                    this.performSwap(tail.prerolled ? 'preroll' : 'no-preroll', 'natural', meta.expectedDisplayTime, null);
                    return;
                }
                if (tail.prerolled)
                    this.armWatchdog(tail);
            }
            b.el.requestVideoFrameCallback(step);
        };
        b.el.requestVideoFrameCallback(step);
    }
    /**
     * ★ v33-e. **프레임 없이 컷을 터뜨린다** — `hls-player.ts` 의 같은 이름과 한 벌이다
     * (2026-09-23 · `docs/68` §2.0-g).
     *
     * 저하 모드(rVFC 없음)에서는 `lastFrameAt` 이 언제나 `null` 이라 **청크 경계를 지나는
     * 순간** 자른다. 전에는 그 모드의 미드롤·라이브 컷이 **원리적으로 안 터졌다** — 아래
     * `stepDegraded` 는 프리롤만 걸고 스왑을 `ended` 에 맡기는데, 회차 중간의 컷에는
     * `ended` 가 오지 않는다.
     */
    cutWithoutFrames(el) {
        const tail = this.tail;
        const front = this.frontBuf;
        if (tail === null || this.swapping || front.el !== el)
            return;
        const due = cutDueWithoutFrames({
            cutAtSec: tail.cutAtSec,
            currentTimeSec: el.currentTime,
            lastFrameAtMs: front.lastFrameAt,
            nowMs: performance.now(),
        });
        if (!due)
            return;
        this.performSwap('timeupdate', 'natural', front.lastFrameEdt, null);
    }
    /**
     * 저하 모드의 꼬리 처리. `timeupdate` 는 약 4Hz 라 마지막 프레임을 잡을 수 없다 —
     * **프리롤만 걸고 스왑은 `ended` 에 맡긴다.** 무갭은 포기하지만 채널은 이어진다.
     * 회차 **중간**의 컷은 위 `cutWithoutFrames` 가 받는다.
     */
    stepDegraded(currentTime) {
        const tail = this.tail;
        if (tail === null || this.swapping || tail.prerolled)
            return;
        if (currentTime < tail.prerollAt)
            return;
        tail.prerolled = true;
        tail.prerollStartedAt = performance.now();
        this.startPreroll();
    }
    /** 대기 버퍼를 **음소거한 채** 재생 시작. 이것이 S4 의 전부다. */
    startPreroll() {
        const back = this.backBuf;
        if (!back.ready || back.playing)
            return;
        const tail = this.tail;
        // v35. 되감을 자리를 적는다 — 근거는 `rewindPreroll`.
        if (tail !== null)
            tail.backStartAt = back.el.currentTime;
        back.el.muted = true;
        back.el
            .play()
            .then(() => {
            /*
             * ★ v35. **`play()` 가 늦게 풀린 사이 프리롤이 되감겼으면 다시 멈춘다.** 같은 꼬리가
             * 아직 서 있는데 `prerolled` 가 내려가 있으면 그것이다(`rewindPreroll`). 꼬리가 바뀌었으면
             * (스왑이 이미 일어났다) 이 버퍼는 앞으로 나간 것이라 그대로 둔다.
             */
            if (tail !== null && this.tail === tail && !tail.prerolled) {
                back.el.pause();
                return;
            }
            back.playing = true;
            this.startWatch(back);
        })
            .catch((e) => {
            // 프리롤 실패는 에러가 아니다. 스왑 루틴이 그 자리에서 play() 를 부른다 (S2 강등).
            back.playing = false;
            void e;
        });
    }
    armWatchdog(tail) {
        if (tail.watchdog !== null)
            clearTimeout(tail.watchdog);
        tail.watchdog = setTimeout(() => {
            if (this.tail !== tail || this.swapping)
                return;
            this.performSwap('watchdog', 'natural', this.frontBuf.lastFrameEdt, null);
        }, WATCHDOG_MS);
    }
    clearTail() {
        if (this.tail?.watchdog !== null && this.tail?.watchdog !== undefined) {
            clearTimeout(this.tail.watchdog);
        }
        this.tail = null;
    }
    /**
     * **경로와 무관하게 스왑은 이 루틴 하나다.** `play()` 가 여기 있는 것이 폴백의 실체다 —
     * 프리롤이 성공했으면 no-op(0ms)이고, 실패했으면 그 자리에서 S2(67ms)가 된다.
     */
    performSwap(path, trigger, aLastEdt, requestedAt) {
        if (this.swapping)
            return false;
        this.swapping = true;
        const tail = this.tail;
        const prerollStartedAt = tail?.prerollStartedAt ?? null;
        this.clearTail();
        const oldFront = this.frontBuf;
        const backIndex = this.backIndex;
        const back = this.backBuf;
        const droppedBefore = droppedFrames(back.el);
        if (!back.playing) {
            back.el
                .play()
                .then(() => {
                back.playing = true;
            })
                .catch((e) => this.errorCb?.(e));
        }
        // 음소거 해제는 노출과 같은 태스크에서 한다.
        back.el.muted = false;
        back.el.style.opacity = '1';
        back.el.style.zIndex = '2';
        oldFront.el.style.opacity = '0';
        oldFront.el.style.zIndex = '1';
        this.front = backIndex;
        const swapAt = performance.now();
        // **A 의 정리를 이 태스크에서 하지 않는다.** 다음 태스크로 미룬다. 미루는 대가로,
        // 코어가 `onSwapped` 직후 부르는 `prepareNext()` 가 이 버퍼를 다시 적재할 수 있다 —
        // 마이크로태스크가 타이머보다 먼저 돈다. **적재 세대가 그대로일 때만** 뗀다.
        //
        // ★ **그 값을 `onSwapped` 보다 먼저 잡는다** (v21/F13, 아이폰 실기가 찾았다).
        // 뒤에서 잡으면 「스왑 당시」가 아니라 **「코어가 손댄 뒤」**이고, 코어가 **동기로**
        // 재적재하는 경로에서는 둘이 다르다 — v21 의 `armAdNext()` 가 `await` 없이
        // `prepareNext()` 를 부르므로 여기 닿기 전에 **이미 새 토큰**이 들어와 있다.
        // 그러면 가드가 통과해 **방금 무장한 버퍼를 떼어 버린다.**
        //
        // **`hls-player.ts` 의 `staleHls` 와 같은 결함이고 같은 고침이다.** 그쪽만 고쳤을 때
        // 노트북은 멀쩡했고 **아이폰에서만** 미드롤 뒤가 깨졌다 — `docs/45` §14 의 1 이
        // 경고한 「어댑터 둘 중 하나만 고친다」가 정확히 이 모양이다.
        const staleToken = oldFront.loadToken;
        // 코어에는 즉시 알린다 — `itemChanged` 가 화면 전환과 같은 프레임에 나가야 UI 가
        // 오버레이를 제때 닫는다. 계측은 아래에서 비동기로 따라온다.
        tail?.opts.onSwapped(path, tail.cutAtSec);
        // **어댑터가 죽은 뒤 발화하면 안 된다** (v19). 엘리먼트를 입양하면 이 콜백이
        // **다음 어댑터가 쓰는 엘리먼트**를 멈추고 음소거한다 — `stop()` 이 `gen` 을 올리므로
        // 그 값을 잡아 두고 대조한다. `TASKS-V12` 가 「취소 불가」라 적어 이 안을 버렸던 자리다.
        const swapGen = this.gen;
        setTimeout(() => {
            if (swapGen !== this.gen)
                return;
            oldFront.el.pause();
            oldFront.el.muted = true;
            oldFront.playing = false;
            if (oldFront.loadToken === staleToken)
                this.detach(oldFront);
            this.startWatch(back);
            this.swapping = false;
        }, 0);
        if (!this.hasRvfc) {
            // 저하 모드는 프레임 시각을 알 수 없다. **수치를 지어내지 않고 `null` 로 남긴다** —
            // `path` 는 그대로 보고되므로 이 층이 쓰였다는 사실은 계측에 드러난다.
            this.transitionCb?.({
                trigger,
                path,
                tFreezeMs: null,
                rawMs: null,
                vsyncMs: this.vsyncMs,
                prerollLeadMs: prerollStartedAt === null ? null : swapAt - prerollStartedAt,
                tSkipMs: null,
                droppedB: null,
            });
            return true;
        }
        // B 의 첫 표시 프레임으로 T_freeze 를 계산한다. M0 과 같은 정의다.
        back.el.requestVideoFrameCallback((_now, meta) => {
            const tBFirst = meta.expectedDisplayTime;
            const V = this.vsyncMs;
            const k = Math.round((FRAME_DURATION_SEC * 1000) / V);
            const raw = aLastEdt === null ? null : tBFirst - aLastEdt;
            this.transitionCb?.({
                trigger,
                path,
                tFreezeMs: raw === null ? null : Math.max(0, Math.round(raw / V) - k) * V,
                rawMs: raw,
                vsyncMs: V,
                prerollLeadMs: prerollStartedAt === null ? null : swapAt - prerollStartedAt,
                tSkipMs: requestedAt === null ? null : tBFirst - requestedAt,
                droppedB: droppedBefore === null ? null : (droppedFrames(back.el) ?? 0) - droppedBefore,
            });
        });
        return true;
    }
    reveal(index) {
        this.buffers.forEach((b, i) => {
            b.el.style.opacity = i === index ? '1' : '0';
            b.el.style.zIndex = i === index ? '2' : '1';
        });
        this.front = index;
    }
    /** vsync 주기 실측. 60Hz 를 가정하지 않는다 — iPhone 7 은 N1 실측 17ms 였다. */
    measureVsync() {
        return new Promise((resolve) => {
            const deltas = [];
            let last = 0;
            const step = (t) => {
                if (last !== 0)
                    deltas.push(t - last);
                last = t;
                if (deltas.length < 30) {
                    requestAnimationFrame(step);
                    return;
                }
                deltas.sort((a, b) => a - b);
                const median = deltas[Math.floor(deltas.length / 2)];
                if (median !== undefined && median > 1 && median < 100)
                    this.vsyncMs = median;
                resolve();
            };
            requestAnimationFrame(step);
        });
    }
}
/**
 * 마지막 비디오 프레임의 mediaTime — `videoStart + videoSpan − 1/30`.
 *
 * `duration` 을 쓰지 않는다. 오디오가 비디오보다 16.67ms 넘게 긴 자산에서는 `duration`
 * 기준 조건이 **한 번도 참이 되지 않아 전환 자체가 일어나지 않는다**
 * (`docs/spike-result.md` 「부채 정산」 3절).
 */
function lastFrameMediaTime(b) {
    if (b.videoStart === null || b.videoSpan === null)
        return null;
    return b.videoStart + b.videoSpan - FRAME_DURATION_SEC;
}
function droppedFrames(el) {
    if (typeof el.getVideoPlaybackQuality !== 'function')
        return null;
    return el.getVideoPlaybackQuality().droppedVideoFrames;
}
/**
 * 매니페스트를 직접 받아 `#EXTINF` 를 더한다. hls.js 의 `LEVEL_LOADED` 자리다.
 *
 * **어댑터 층이므로 `fetch` 를 써도 된다** — 금지는 `core/` 다 (절대 규칙 6).
 * HLS 는 `Access-Control-Allow-Origin: *` 로 서빙되므로(`docs/03` §5) 오리진이 달라도 읽힌다.
 *
 * **던지지 않는다.** 실패하면 `null` 이고 그때는 예측 스왑을 잃을 뿐이다 — 프리롤은
 * `duration` 폴백으로 걸리고 스왑은 워치독이 받는다. 재생을 막을 이유가 아니다.
 */
async function fetchSegments(url, timeoutMs = MANIFEST_TIMEOUT_MS) {
    const abort = new AbortController();
    const timer = setTimeout(() => {
        abort.abort();
    }, timeoutMs);
    try {
        const res = await fetch(url, { credentials: 'omit', signal: abort.signal });
        if (!res.ok)
            return null;
        const text = await res.text();
        /*
         * ★★ v33. **라이브면 `null` 이다** (`docs/67` §7.6 ② · 절대 규칙 84).
         *
         * 판정은 **`#EXT-X-ENDLIST` 의 유무** 하나다 — 웹 어댑터가 `details.live` 로 아는
         * 그 사실을 여기서는 매니페스트가 직접 말한다(D12 — 계약을 넓히지 않는다).
         *
         * 라이브 매니페스트의 `#EXTINF` 합은 **창(약 20초)의 것**이라, 그대로 `videoSpan` 에
         * 넣으면 `lastFrameMediaTime` 이 「20초 뒤가 영상 끝」이라고 말하고 **붙자마자 꼬리
         * 스왑이 터진다.** `null` 이면 이 어댑터는 예측 스왑을 포기하고, 라이브에서는 그것이
         * 옳다 — 복귀는 `swapNow('live')` 가 한다.
         */
        if (!isFinishedManifest(text))
            return null;
        const durations = parseExtInf(text);
        return durations.length === 0 ? null : durations;
    }
    catch {
        return null;
    }
    finally {
        clearTimeout(timer);
    }
}
/**
 * v33. **끝이 있는 매니페스트인가** — `#EXT-X-ENDLIST` 의 유무.
 *
 * 그 태그 하나가 VOD 와 라이브를 가른다(`onair-server` 의 `playlist.ts` 가 그것을 **안
 * 붙이는 것**으로 라이브를 만든다). 슬라이딩 윈도에는 없고, 끝난 라이브(유예 구간)에는 있다.
 */
/**
 * ★ v33-c. **이 실패를 되살릴 것인가** (2026-09-22).
 *
 * 어댑터 본체는 DOM·미디어 타이밍이라 node 에서 검증할 수 없다(이 파일의 머리 주석).
 * 그래서 **판정만 밖으로 뺀다** — 걸리는 것이 둘이고 둘 다 틀리면 증상이 조용하다:
 * 토큰을 안 보면 **끈 채널이 되살아나고**, 상한을 안 두면 **영영 다시 건다**(그 화면은
 * 오류도 안 뜨고 그냥 검다).
 */
export function retriesArm(input) {
    const { token, currentToken, attempts, max = ARM_ATTEMPTS } = input;
    if (token !== currentToken)
        return false;
    return attempts < max;
}
export function isFinishedManifest(manifest) {
    return /^#EXT-X-ENDLIST\s*$/m.test(manifest);
}
/**
 * `#EXTINF` 합. **비디오 스팬이지 `duration` 이 아니다** — 오디오 꼬리를 포함하지 않는 것이
 * 이 값을 쓰는 이유 전부다.
 *
 * 마스터 플레이리스트(`#EXT-X-STREAM-INF`)가 오면 `#EXTINF` 가 하나도 없어 `null` 이다.
 * **변형을 따라가지 않는다** — 지금 인코딩은 단일 화질이고(절대 규칙 4) 그런 자산이 없다.
 * 훗날 ABR 이 들어오면 예측 스왑을 잃고 워치독으로 내려가는데, 그 강등은 `SwapPath` 에
 * `watchdog` 이 잦아지는 것으로 드러난다.
 */
export function sumExtInf(manifest) {
    const durations = parseExtInf(manifest);
    return durations.length === 0 ? null : durations.reduce((a, b) => a + b, 0);
}
/**
 * `#EXTINF` 목록 (v16). **`sumExtInf` 가 이것으로 유도된다** — 파싱이 두 곳에 있으면
 * 하나만 고쳐지고, 그때 스팬과 정렬이 서로 다른 매니페스트를 본 것처럼 어긋난다.
 *
 * 청크 정렬(`alignToChunkStart`)이 요구하는 것이 이 배열이다. hls.js 쪽은
 * `LEVEL_LOADED` 의 `details.fragments` 가 같은 값을 준다 — 그쪽은 파싱하지 않는다.
 */
export function parseExtInf(manifest) {
    const durations = [];
    for (const line of manifest.split('\n')) {
        if (!line.startsWith('#EXTINF:'))
            continue;
        // `#EXTINF:2.000000,` — `parseFloat` 가 쉼표에서 멈춘다.
        const seconds = Number.parseFloat(line.slice('#EXTINF:'.length));
        if (!Number.isFinite(seconds) || seconds <= 0)
            continue;
        durations.push(seconds);
    }
    return durations;
}
//# sourceMappingURL=native-hls-player.js.map